import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, CalendarClock, Download, X } from 'lucide-react';
import { staffClient } from 'clients/staff';
import SiteSearchBar from 'ui/SiteSearchBar';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import TableContainer from 'ui/TableContainer';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import { Badge, Button } from 'ui/shadcn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'ui/shadcn/tooltip';
import { cn } from 'lib/utils';
import {
    SiteCategory,
    SITE_CATEGORY_BADGE,
    SITE_CATEGORY_LABEL,
    isSiteCategory,
} from 'apis/site';
import TimelineModal from './TimelineModal';
import CustomFilterPanel, {
    CustomFilter,
    SavedFilter,
    countActive,
    matchesCustomFilter,
    loadSavedFilters,
} from './CustomFilterPanel';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface WorkTypeStaff {
    chief: string[];
    assistant: string[];
}

interface StaffByWorkType {
    ELECTRIC: WorkTypeStaff;
    TELECOM: WorkTypeStaff;
    FIRE: WorkTypeStaff;
}

const StaffTooltip: React.FC<{
    value: number;
    staffByWorkType: StaffByWorkType;
}> = ({ value, staffByWorkType }) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLSpanElement>(null);

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPosition({ top: rect.top - 8, left: rect.left + rect.width / 2 });
        }
        setShow(true);
    };

    const tooltip = show
        ? ReactDOM.createPortal(
              <div
                  className="fixed z-[9999] pointer-events-none"
                  style={{
                      top: position.top,
                      left: position.left,
                      transform: 'translate(-50%, -100%)',
                  }}
              >
                  <div className="bg-popover text-popover-foreground text-xs rounded border border-border p-3 whitespace-nowrap">
                      <table className="min-w-[200px]">
                          <thead>
                              <tr className="border-b border-border">
                                  <th className="py-1 pr-3 text-left font-semibold text-muted-foreground">
                                      공종
                                  </th>
                                  <th className="py-1 px-2 text-left font-semibold text-muted-foreground">
                                      책임
                                  </th>
                                  <th className="py-1 pl-2 text-left font-semibold text-muted-foreground">
                                      보조
                                  </th>
                              </tr>
                          </thead>
                          <tbody>
                              {(
                                  [
                                      ['ELECTRIC', '전기', 'bg-amber-500'],
                                      ['TELECOM', '통신', 'bg-info'],
                                      ['FIRE', '소방', 'bg-destructive'],
                                  ] as const
                              ).map(([key, label, dot]) => {
                                  const t = staffByWorkType[key as keyof StaffByWorkType];
                                  return (
                                      <tr key={key}>
                                          <td className="pr-3 py-1 font-medium">
                                              <span className={cn('inline-block w-2 h-2 rounded-full mr-1.5', dot)} />
                                              {label}
                                          </td>
                                          <td className="px-2 py-1">
                                              {t.chief.length > 0 ? t.chief.join(', ') : '-'}
                                          </td>
                                          <td className="pl-2 py-1">
                                              {t.assistant.length > 0 ? t.assistant.join(', ') : '-'}
                                          </td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                  </div>
              </div>,
              document.body
          )
        : null;

    return (
        <>
            <span
                ref={triggerRef}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full bg-navy-50 text-primary text-sm font-medium tabular-nums hover:bg-accent transition-colors cursor-default"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setShow(false)}
            >
                {value}명
            </span>
            {tooltip}
        </>
    );
};

type StatusTab = 'ALL' | 'IN_PROGRESS' | 'CONTRACT_COMPLETED' | 'COMPLETED';
type PeriodFilter = 'NONE' | 'START_1Y' | 'END_1Y' | 'COMPLETED_5Y';

interface ContractInfo {
    revision: number;
    contractDate: string;
    workType?: string;
}

interface Site {
    id: number;
    name: string;
    location?: string;
    address?: string;
    type: string;
    category?: SiteCategory;
    constructionStartDate?: string;
    constructionEndDate?: string;
    contractDate?: string;
    allContracts?: ContractInfo[];
    supervisionStartDate?: string;
    supervisionEndDate?: string;
    totalFloorArea?: string;
    numberOfUnits?: string;
    buildingScale?: string;
    progress: number;
    progressRate?: string | number;
    staffCount: number;
    staffByWorkType?: StaffByWorkType;
    chiefSupervisor?: string;
    contractorName?: string;
    workTypes?: string[];
}

const SiteList = () => {
    const navigate = useNavigate();
    // ?category=SUPERVISION|CONSTRUCTION|DESIGN — 감리/공사/설계 현장자료실 진입
    const [searchParams] = useSearchParams();
    const categoryParam = searchParams.get('category');
    const category: SiteCategory | null = isSiteCategory(categoryParam) ? categoryParam : null;
    const categoryLabel = category ? SITE_CATEGORY_LABEL[category] : null;
    const [sites, setSites] = useState<Site[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<StatusTab>('ALL');
    const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('NONE');
    const [customFilter, setCustomFilter] = useState<CustomFilter>({});
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
    const [showTimeline, setShowTimeline] = useState(false);
    const [userType, setUserType] = useState('');

    useEffect(() => {
        setSavedFilters(loadSavedFilters());
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchSites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    const fetchSites = async () => {
        try {
            const [siteResponse, myInfoResponse] = await Promise.all([
                staffClient.get('/site', {
                    params: { limit: 1000, ...(category ? { category } : {}) },
                }),
                staffClient.get('/auth/my-info'),
            ]);

            const type = myInfoResponse.data?.token?.type || '';
            setUserType(type);

            const allSites = siteResponse.data?.data || [];
            let visibleSites = allSites.filter((site: any) => site.visible !== false);

            if (type === 'STAFF') {
                const mySiteIds: number[] = await staffClient
                    .get('/auth/my-sites')
                    .then((r) => r.data);
                visibleSites = visibleSites.filter((site: any) => mySiteIds.includes(site.id));
            }

            const sitesData: Site[] = visibleSites.map((site: any, index: number) => {
                let progressValue = 0;
                if (site.progressRate !== undefined && site.progressRate !== null) {
                    if (typeof site.progressRate === 'string') {
                        progressValue = parseInt(site.progressRate.replace('%', '')) || 0;
                    } else {
                        progressValue = Number(site.progressRate) || 0;
                    }
                }
                return {
                    id: site.id || index + 1,
                    name: site.name || `현장 ${index + 1}`,
                    address: site.location || site.address || '위치 정보 없음',
                    type: site.type || 'ETC',
                    category: site.category || 'SUPERVISION',
                    constructionStartDate: site.constructionStartDate || '',
                    constructionEndDate: site.constructionEndDate || '',
                    contractDate: site.contractDate || '',
                    allContracts: site.allContracts || [],
                    supervisionStartDate: site.supervisionStartDate || '',
                    supervisionEndDate: site.supervisionEndDate || '',
                    totalFloorArea: site.totalFloorArea || '',
                    numberOfUnits: site.numberOfUnits || '',
                    buildingScale: site.buildingScale || '',
                    progress: progressValue,
                    progressRate: site.progressRate,
                    staffCount: site.staffCount || 0,
                    staffByWorkType: site.staffByWorkType || {
                        ELECTRIC: { chief: [], assistant: [] },
                        TELECOM: { chief: [], assistant: [] },
                        FIRE: { chief: [], assistant: [] },
                    },
                    chiefSupervisor: site.chiefSupervisor,
                    contractorName: site.contractorName || '',
                    workTypes: site.workTypes || [],
                };
            });
            setSites(sitesData);
        } catch (error) {
            console.error('Failed to fetch sites:', error);
        } finally {
            setLoading(false);
        }
    };

    const now = new Date();
    const oneYearFromNow = new Date(now);
    oneYearFromNow.setFullYear(now.getFullYear() + 1);
    const fiveYearsAgo = new Date(now);
    fiveYearsAgo.setFullYear(now.getFullYear() - 5);
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(now.getFullYear() - 1);

    const parseDate = (s?: string) => (s ? new Date(s) : null);

    const isCompletedWithinYears = (site: Site, years: number) => {
        if (site.type !== 'COMPLETED') return false;
        const endDate = site.constructionEndDate || site.supervisionEndDate;
        if (!endDate) return false;
        const end = new Date(endDate);
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - years);
        return end >= cutoff;
    };

    // 기간 필터 (탭과 AND 조합)
    const matchesPeriod = (site: Site, filter: PeriodFilter): boolean => {
        if (filter === 'NONE') return true;
        if (filter === 'START_1Y') {
            const start = parseDate(site.constructionStartDate);
            return !!start && start >= now && start <= oneYearFromNow;
        }
        if (filter === 'END_1Y') {
            const end = parseDate(site.constructionEndDate);
            return !!end && end >= now && end <= oneYearFromNow;
        }
        if (filter === 'COMPLETED_5Y') {
            return isCompletedWithinYears(site, 5);
        }
        return true;
    };

    // 탭별 기본 필터 + 기간 필터 + 상세 필터 + 검색 (연도 제한 없음 — 과거 현장도 모두 노출)
    const filteredSites = sites
        .filter((site) => {
            if (activeTab !== 'ALL' && site.type !== activeTab) return false;
            if (!matchesPeriod(site, periodFilter)) return false;
            if (!matchesCustomFilter(site, customFilter)) return false;
            const term = searchTerm.toLowerCase();
            if (
                term &&
                !site.name.toLowerCase().includes(term) &&
                !(site.address || '').toLowerCase().includes(term)
            ) {
                return false;
            }
            return true;
        })
        .sort((a, b) => {
            // 전체 탭은 진행중 → 착공예정 → 준공 → 기타 순으로 묶고, 묶음 안에서는 각 상태의 정렬 규칙을 쓴다
            const groupRank = (type: string) =>
                type === 'IN_PROGRESS' ? 0 : type === 'CONTRACT_COMPLETED' ? 1 : type === 'COMPLETED' ? 2 : 3;
            const sortKey = activeTab === 'ALL' ? a.type : activeTab;
            if (activeTab === 'ALL') {
                const diff = groupRank(a.type) - groupRank(b.type);
                if (diff !== 0) return diff;
            }

            // 탭별 기본 정렬
            switch (sortKey) {
                case 'IN_PROGRESS': {
                    // 준공 임박순: 준공일 오름차순 (null 뒤로)
                    const av = a.constructionEndDate || '￿';
                    const bv = b.constructionEndDate || '￿';
                    return av < bv ? -1 : av > bv ? 1 : 0;
                }
                case 'CONTRACT_COMPLETED': {
                    // 착공 임박순: 착공일 오름차순, 없으면 가나다
                    const as = a.constructionStartDate;
                    const bs = b.constructionStartDate;
                    if (!as && !bs) return a.name.localeCompare(b.name);
                    if (!as) return 1;
                    if (!bs) return -1;
                    return as < bs ? -1 : as > bs ? 1 : 0;
                }
                case 'COMPLETED': {
                    // 최근 준공순: 종료일 내림차순
                    const av = a.constructionEndDate || '';
                    const bv = b.constructionEndDate || '';
                    return av > bv ? -1 : av < bv ? 1 : 0;
                }
            }
        });

    // 상태 3종 톤다운:
    //  진행중 → success (연 green)
    //  착공예정(계약완료) → warning (연 amber)
    //  준공 → secondary (연 neutral, 완료된 것은 조용한 회색톤)
    const getTypeBadgeVariant = (
        type: string
    ): 'success' | 'warning' | 'secondary' => {
        switch (type) {
            case 'CONTRACT_COMPLETED':
                return 'warning';
            case 'IN_PROGRESS':
                return 'success';
            case 'COMPLETED':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    // 공종 라벨/톤
    const workTypeBadgeMap: Record<
        string,
        { label: string; variant: 'warning' | 'danger' | 'info' | 'success' | 'secondary' }
    > = {
        ELECTRIC: { label: '전기', variant: 'warning' },
        FIRE: { label: '소방', variant: 'danger' },
        TELECOM: { label: '통신', variant: 'info' },
        NETWORK: { label: '통신', variant: 'info' },
    };

    // 감리 시작일·종료일 기반 자동 경과율 (0~100). 값 없으면 null → 수동값 fallback
    const computeElapsedRate = (start?: string, end?: string): number | null => {
        if (!start || !end) return null;
        const s = new Date(start).getTime();
        const e = new Date(end).getTime();
        if (isNaN(s) || isNaN(e) || e <= s) return null;
        const t = Date.now();
        if (t <= s) return 0;
        if (t >= e) return 100;
        return Math.round(((t - s) / (e - s)) * 100);
    };

    // 감리원 축약: "이수빈 외 N명" (책임 우선, 없으면 보조)
    const summarizeStaff = (byWorkType: StaffByWorkType, total: number) => {
        const allChiefs = [
            ...byWorkType.ELECTRIC.chief,
            ...byWorkType.TELECOM.chief,
            ...byWorkType.FIRE.chief,
        ];
        const allAssistants = [
            ...byWorkType.ELECTRIC.assistant,
            ...byWorkType.TELECOM.assistant,
            ...byWorkType.FIRE.assistant,
        ];
        const first = allChiefs[0] || allAssistants[0];
        if (!first) return `${total}명`;
        const rest = total - 1;
        return rest > 0 ? `${first} 외 ${rest}명` : first;
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'CONTRACT_COMPLETED':
                return '계약완료';
            case 'IN_PROGRESS':
                return '진행중';
            case 'COMPLETED':
                return '준공';
            case 'ETC':
                return '기타';
            default:
                return type || '-';
        }
    };

    const handleSiteClick = (siteId: number) => {
        navigate(`/sites/${siteId}`);
    };

    // 3탭 정의
    const tabs: { key: StatusTab; label: string; count: number }[] = [
        {
            key: 'ALL',
            label: '전체',
            count: sites.length,
        },
        {
            key: 'IN_PROGRESS',
            label: '진행중',
            count: sites.filter((s) => s.type === 'IN_PROGRESS').length,
        },
        {
            key: 'CONTRACT_COMPLETED',
            label: '착공예정',
            count: sites.filter((s) => s.type === 'CONTRACT_COMPLETED').length,
        },
        {
            key: 'COMPLETED',
            label: '준공',
            count: sites.filter((s) => s.type === 'COMPLETED').length,
        },
    ];

    // 기간 필터 카드 3장 — 각각 어울리는 탭으로 자동 전환
    const periodCards: {
        key: PeriodFilter;
        label: string;
        subtitle: string;
        count: number;
        forTab: StatusTab;
    }[] = [
        {
            key: 'START_1Y',
            label: '1년 내 착공예정',
            subtitle: '착공일 오늘~+1년',
            forTab: 'CONTRACT_COMPLETED',
            count: sites.filter(
                (s) => s.type === 'CONTRACT_COMPLETED' && matchesPeriod(s, 'START_1Y')
            ).length,
        },
        {
            key: 'END_1Y',
            label: '1년 내 준공예정',
            subtitle: '준공일 오늘~+1년',
            forTab: 'IN_PROGRESS',
            count: sites.filter((s) => s.type === 'IN_PROGRESS' && matchesPeriod(s, 'END_1Y'))
                .length,
        },
        {
            key: 'COMPLETED_5Y',
            label: '5년 내 준공',
            subtitle: '최근 5년',
            forTab: 'COMPLETED',
            count: sites.filter((s) => matchesPeriod(s, 'COMPLETED_5Y')).length,
        },
    ];

    const handlePeriodCardClick = (card: (typeof periodCards)[number]) => {
        // 이미 active면 필터 해제, 아니면 그 카드로 전환 (탭도 함께)
        if (periodFilter === card.key && activeTab === card.forTab) {
            setPeriodFilter('NONE');
        } else {
            setActiveTab(card.forTab);
            setPeriodFilter(card.key);
        }
    };

    const handleTabChange = (tab: StatusTab) => {
        setActiveTab(tab);
        // 다른 탭으로 넘어가면 기간 필터 해제 (매칭 안 되는 조합 방지)
        setPeriodFilter('NONE');
    };

    const handleExcelDownload = () => {
        const activeLabel = tabs.find((t) => t.key === activeTab)?.label || '전체';
        const data = filteredSites.map((site, idx) => ({
            No: idx + 1,
            현장명: site.name,
            구분: SITE_CATEGORY_LABEL[(site.category || 'SUPERVISION') as SiteCategory],
            상태: getTypeLabel(site.type),
            진행률: `${site.progress}%`,
            감리원수: site.staffCount,
            시공사: site.contractorName || '',
            계약일: formatDate(site.contractDate || ''),
            감리시작: formatDate(site.supervisionStartDate || ''),
            감리종료: formatDate(site.supervisionEndDate || ''),
            연면적: site.totalFloorArea || '',
            세대수: site.numberOfUnits || '',
            건축규모: site.buildingScale || '',
            주소: site.address || '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '현장목록');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/octet-stream' }),
            `${categoryLabel ? categoryLabel + '현장' : '현장목록'}_${activeLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };

    const formatDate = (date: string) => {
        if (!date) return '-';
        const d = new Date(date);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    const categoryColumn: Column<Site> = {
        key: 'category',
        header: '구분',
        width: '70px',
        align: 'center',
        render: (value) => {
            const badge = SITE_CATEGORY_BADGE[(value || 'SUPERVISION') as SiteCategory];
            return <Badge variant={badge?.variant || 'secondary'}>{badge?.label || value || '-'}</Badge>;
        },
    };

    const columns: Column<Site>[] = [
        {
            key: 'name',
            header: '현장명',
            width: '280px',
            sortable: true,
            render: (value, row) => (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSiteClick(row.id);
                    }}
                    className="text-left font-semibold text-foreground hover:text-primary transition-colors duration-fast underline-offset-2 hover:underline"
                    title={value}
                >
                    {value}
                </button>
            ),
        },
        {
            key: 'type',
            header: '상태',
            width: '90px',
            align: 'center',
            render: (value) => (
                <Badge variant={getTypeBadgeVariant(value)}>{getTypeLabel(value)}</Badge>
            ),
        },
        {
            key: 'workTypes',
            header: '공종',
            width: '120px',
            align: 'center',
            render: (_value, row) => {
                if (!row.workTypes || row.workTypes.length === 0) {
                    return <span className="text-neutral-400">-</span>;
                }
                return (
                    <div className="inline-flex flex-wrap justify-center gap-1">
                        {row.workTypes.map((wt) => {
                            const b = workTypeBadgeMap[wt];
                            if (!b) return null;
                            return (
                                <Badge key={wt} variant={b.variant} className="text-[10px] px-1.5 py-0">
                                    {b.label}
                                </Badge>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            key: 'progress',
            header: '진행률',
            width: '110px',
            align: 'right',
            sortable: true,
            render: (value, row) => {
                const auto = computeElapsedRate(
                    row.supervisionStartDate,
                    row.supervisionEndDate
                );
                const rate = (n: number) => (
                    <span className="text-base font-medium tabular-nums text-foreground">
                        {n}
                        <span className="text-sm text-muted-foreground ml-0.5">%</span>
                    </span>
                );
                if (auto === null) return rate(Number(value) || 0);
                // 자동 계산값: 텍스트 배지 대신 달력-시계 아이콘 + 호버 시 계산 근거(감리 기간)
                return (
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex items-center justify-end gap-1">
                                    <CalendarClock
                                        className="h-3.5 w-3.5 text-neutral-400"
                                        aria-label="감리 기간 기준 자동 계산"
                                    />
                                    {rate(auto)}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                                감리 기간 경과율 · {formatDate(row.supervisionStartDate)} ~{' '}
                                {formatDate(row.supervisionEndDate)}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            },
        },
        {
            key: 'staffCount',
            header: '감리원',
            width: '140px',
            render: (value, row) => {
                const defaultStaffByWorkType: StaffByWorkType = {
                    ELECTRIC: { chief: [], assistant: [] },
                    TELECOM: { chief: [], assistant: [] },
                    FIRE: { chief: [], assistant: [] },
                };
                const staffByWorkType = row.staffByWorkType || defaultStaffByWorkType;
                if (value <= 0) return <span className="text-neutral-400">-</span>;
                return (
                    <div className="inline-flex items-center gap-1.5">
                        <StaffTooltip value={value} staffByWorkType={staffByWorkType} />
                        <span className="text-sm text-muted-foreground truncate">
                            {summarizeStaff(staffByWorkType, value)}
                        </span>
                    </div>
                );
            },
        },
        {
            key: 'contractorName',
            header: '시공사',
            width: '120px',
            render: (value) => (
                <span className="text-base">{value || <span className="text-neutral-400">-</span>}</span>
            ),
        },
        {
            key: 'contractDate',
            header: '계약일',
            width: '140px',
            render: (value, row) => {
                const contractCount = row.allContracts?.length || 0;
                const hasMultiple = contractCount > 1;
                return (
                    <div className="flex items-center gap-1">
                        <span className="text-base tabular-nums">{formatDate(value)}</span>
                        {hasMultiple && (
                            <div className="relative group">
                                <span className="px-1.5 bg-navy-50 text-primary text-[10px] font-semibold rounded cursor-default tabular-nums">
                                    +{contractCount - 1}
                                </span>
                                <div className="absolute z-50 hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-popover border border-border rounded p-2">
                                    <div className="text-xs font-semibold text-neutral-700 border-b border-border pb-1 mb-1">
                                        계약 목록
                                    </div>
                                    {row.allContracts?.map((contract, idx) => (
                                        <div
                                            key={idx}
                                            className="flex justify-between text-xs py-0.5 tabular-nums"
                                        >
                                            <span className="text-muted-foreground">
                                                {contract.revision}차
                                            </span>
                                            <span className="text-foreground">
                                                {formatDate(contract.contractDate)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'supervisionStartDate',
            header: '감리시작',
            width: '110px',
            align: 'center',
            render: (value) => <span className="text-base tabular-nums">{formatDate(value)}</span>,
        },
        {
            key: 'supervisionEndDate',
            header: '감리종료',
            width: '110px',
            align: 'center',
            render: (value) => <span className="text-base tabular-nums">{formatDate(value)}</span>,
        },
        {
            key: 'totalFloorArea',
            header: '연면적',
            width: '100px',
            align: 'center',
            render: (value) =>
                value ? <span className="text-base">{value}</span> : <span className="text-neutral-400">-</span>,
        },
        {
            key: 'numberOfUnits',
            header: '세대수',
            width: '90px',
            align: 'center',
            render: (value) =>
                value ? (
                    <span className="text-base tabular-nums">{value}</span>
                ) : (
                    <span className="text-neutral-400">-</span>
                ),
        },
        {
            key: 'buildingScale',
            header: '건축규모',
            width: '100px',
            align: 'center',
            render: (value) =>
                value ? <span className="text-base">{value}</span> : <span className="text-neutral-400">-</span>,
        },
    ];

    if (loading) {
        return (
            <PageContainer>
                <Loading text="현장 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title={categoryLabel ? `${categoryLabel}현장` : '현장 목록'}
                subtitle={`전체 ${sites.length}개 현장`}
                actions={<SiteSearchBar />}
            />

            {/* 상태 탭 — 전체 다음에 구분선, 모양은 동일 */}
            <div className="mb-4 flex items-center gap-1 border-b border-border">
                {tabs.map((tab) => {
                    const active = activeTab === tab.key;
                    return (
                        <React.Fragment key={tab.key}>
                            <button
                                type="button"
                                onClick={() => handleTabChange(tab.key)}
                                className={cn(
                                    'relative -mb-px inline-flex items-center gap-2 px-4 h-10 text-md font-medium transition-colors duration-fast border-b-2',
                                    active
                                        ? 'border-primary text-foreground'
                                        : 'border-transparent text-neutral-500 hover:text-foreground'
                                )}
                            >
                                <span>{tab.label}</span>
                                <span
                                    className={cn(
                                        'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold tabular-nums',
                                        active
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-neutral-600'
                                    )}
                                >
                                    {tab.count}
                                </span>
                            </button>
                            {tab.key === 'ALL' && (
                                <span className="mx-2 mb-1 h-5 w-px bg-border" aria-hidden="true" />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* 기간별 부가 카드 3장 — MASTER에게만 (STAFF는 배정 현장만 보이므로 카드 불필요) */}
            {userType === 'MASTER' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    {periodCards.map((card) => {
                        const active =
                            periodFilter === card.key && activeTab === card.forTab;
                        return (
                            <button
                                key={card.key}
                                type="button"
                                onClick={() => handlePeriodCardClick(card)}
                                className={cn(
                                    'p-3 rounded border text-left transition-colors duration-fast',
                                    active
                                        ? 'bg-navy-50 border-navy-100'
                                        : 'bg-card border-border hover:border-neutral-300'
                                )}
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <div
                                        className={cn(
                                            'text-sm font-medium truncate',
                                            active ? 'text-primary' : 'text-foreground'
                                        )}
                                    >
                                        {card.label}
                                    </div>
                                    <div
                                        className={cn(
                                            'text-lg font-semibold tabular-nums shrink-0',
                                            active ? 'text-primary' : 'text-foreground'
                                        )}
                                    >
                                        {card.count}
                                        <span className="text-xs font-normal text-muted-foreground ml-0.5">
                                            건
                                        </span>
                                    </div>
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {card.subtitle}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 저장된 필터 chips */}
            {savedFilters.length > 0 && (
                <div className="mb-4 flex items-center gap-2 text-sm flex-wrap">
                    <label className="text-muted-foreground shrink-0">내 필터</label>
                    <div className="flex flex-wrap gap-1 flex-1">
                        {savedFilters.map((sf) => {
                            const isActive =
                                JSON.stringify(sf.filter) === JSON.stringify(customFilter);
                            return (
                                <span
                                    key={sf.id}
                                    className={cn(
                                        'inline-flex items-center h-7 pl-2.5 pr-1 rounded text-xs font-medium transition-colors duration-fast',
                                        isActive
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-neutral-700 hover:bg-accent hover:text-primary'
                                    )}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setCustomFilter(sf.filter)}
                                        className="pr-1.5"
                                        title={`${sf.name} 적용`}
                                    >
                                        {sf.name}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = savedFilters.filter(
                                                (f) => f.id !== sf.id
                                            );
                                            setSavedFilters(next);
                                            try {
                                                localStorage.setItem(
                                                    'ywenf.site-list.saved-filters',
                                                    JSON.stringify(next)
                                                );
                                            } catch {}
                                        }}
                                        className={cn(
                                            'inline-flex items-center justify-center h-5 w-5 rounded transition-colors',
                                            isActive
                                                ? 'hover:bg-navy-500'
                                                : 'hover:bg-neutral-300'
                                        )}
                                        title="삭제"
                                        aria-label={`${sf.name} 삭제`}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </span>
                            );
                        })}
                        {countActive(customFilter) > 0 && (
                            <button
                                type="button"
                                onClick={() => setCustomFilter({})}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs text-muted-foreground hover:text-foreground transition-colors"
                                title="상세 필터 초기화"
                            >
                                <X className="h-3 w-3" />
                                필터 초기화
                            </button>
                        )}
                    </div>
                </div>
            )}

            <TableContainer
                title={categoryLabel ? `${categoryLabel}현장 목록` : '현장 목록'}
                resultCount={filteredSites.length}
                actions={
                    <div className="flex gap-2">
                        <CustomFilterPanel
                            filter={customFilter}
                            onChange={setCustomFilter}
                            savedFilters={savedFilters}
                            onSavedFiltersChange={setSavedFilters}
                        />
                        <Button variant="secondary" size="sm" onClick={handleExcelDownload}>
                            <Download />
                            엑셀
                        </Button>
                        <Button size="sm" onClick={() => setShowTimeline(true)}>
                            <Calendar />
                            공사기간 현황
                        </Button>
                    </div>
                }
            >
                <Table
                    columns={category ? columns : [columns[0], categoryColumn, ...columns.slice(1)]}
                    data={filteredSites}
                    pagination
                    pageSize={20}
                    stickyHeader
                    maxHeight="calc(100vh - 340px)"
                    emptyMessage="검색 결과가 없습니다"
                    onRowClick={(site) => handleSiteClick(site.id)}
                />
            </TableContainer>

            <TimelineModal isOpen={showTimeline} onClose={() => setShowTimeline(false)} />
        </PageContainer>
    );
};

export default SiteList;
